import BaseDetailView from '../shared/BaseDetailView';
import Badge from '../shared/Badge';
import NavigationArrow from '../shared/NavigationArrow';
import SearchableAccessList from '../shared/SearchableAccessList';
import DetailsTabLayout, { FieldSection, SectionDivider } from '../shared/DetailsTabLayout';
import DetailViewActionButtons from '../shared/DetailViewActionButtons';
import { ICONS } from '../../constants/detailViewConstants';
import { useState, useLayoutEffect } from 'react';
import { useUIState } from '../../contexts/UIStateContext';
import { capitalizeFirstLetter } from '../../utils/textFormat';
import { useDetailViewActions } from '../../hooks/useDetailViewActions.jsx';
import { getSubTheme } from '../../constants/subThemes';

export default function RoleDetailView({ role, onDelete, onPrevious = null, onNext = null, theme = 'purple' }) {
  const themeConfig = getSubTheme(theme);
  const { createDeleteAction } = useDetailViewActions();
  const { getAdminRoleTab, setAdminRoleTab } = useUIState();
  const [activeTab, setActiveTab] = useState('details');

  // Restore saved tab when role changes
  useLayoutEffect(() => {
    if (role?.id) {
      const savedTab = getAdminRoleTab(role.id);
      setActiveTab(savedTab);
    }
  }, [role?.id, getAdminRoleTab]);

  // Wrapper to persist active tab to UIState (per role)
  const handleSetActiveTab = (tab) => {
    setActiveTab(tab);
    if (role?.id) {
      setAdminRoleTab(role.id, tab);
    }
  };

  // Header content with icon, title, and display name
  const headerContent = (
    <div className="flex items-center space-x-4">
      {/* Navigation Arrow - Previous */}
      {(onPrevious || onNext) && (
        <NavigationArrow
          direction="previous"
          onClick={onPrevious}
          disabled={!onPrevious}
          label={onPrevious ? 'Previous role' : 'No previous role'}
        />
      )}

      <div className={`p-3 ${themeConfig.iconBg} rounded-lg`}>
        <svg className={`w-8 h-8 ${themeConfig.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      </div>
      <div>
        <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
          {capitalizeFirstLetter(role?.name) || 'Role'}
        </h2>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="gray">
            {role?.users?.length || 0} {role?.users?.length === 1 ? 'user' : 'users'}
          </Badge>
          <Badge variant="gray">
            {role?.permission_ids?.length || 0} {role?.permission_ids?.length === 1 ? 'permission' : 'permissions'}
          </Badge>
        </div>
      </div>

      {/* Navigation Arrow - Next */}
      {(onPrevious || onNext) && (
        <NavigationArrow
          direction="next"
          onClick={onNext}
          disabled={!onNext}
          label={onNext ? 'Next role' : 'No next role'}
        />
      )}
    </div>
  );

  // Custom tabs for roles - Details, Users, Products
  const tabs = [
    { id: 'details', label: 'Details', icon: ICONS.details },
    { id: 'users', label: 'Users', icon: ICONS.access },
    { id: 'products', label: 'Products', icon: ICONS.products }
  ];

  // Render user card
  const renderUserCard = (user) => (
    <div
      key={user.id}
      className={`rounded-lg p-4 ${themeConfig.hoverBorder} transition-all`}
      style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-default)', borderWidth: '1px' }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{user.username}</h4>
            {user.full_name && (
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>({user.full_name})</span>
            )}
            {!user.is_active && (
              <Badge variant="inactive">
                Inactive
              </Badge>
            )}
          </div>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{user.email}</p>
        </div>
        <div className={`flex-shrink-0 w-3 h-3 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-gray-600'}`} />
      </div>
    </div>
  );

  // Render product card (placeholder for now)
  const renderProductCard = (product) => (
    <div
      key={product.id}
      className={`rounded-lg p-4 ${themeConfig.hoverBorder} transition-all`}
      style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-default)', borderWidth: '1px' }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{product.name}</h4>
          </div>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{product.category}</p>
        </div>
        <div className="flex-shrink-0 w-3 h-3 rounded-full bg-green-500" />
      </div>
    </div>
  );

  // Render content based on active tab
  const renderContent = () => {
    if (activeTab === 'details') {
      if (!role) {
        return (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <p style={{ color: 'var(--color-text-muted)' }}>Select a role to view details</p>
          </div>
        );
      }
      return (
        <DetailsTabLayout
          title="Role Details"
          subtitle="View role information and permissions"
          showEditButtons={false}
        >
          <FieldSection label="Role Name">
            <div className="w-full h-11 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 flex items-center text-white text-sm">
              {capitalizeFirstLetter(role.name)}
            </div>
          </FieldSection>

          <SectionDivider />

          <FieldSection label="Theme">
            <div className="w-full h-11 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 flex items-center text-white text-sm">
              {capitalizeFirstLetter(role.theme)}
            </div>
          </FieldSection>
        </DetailsTabLayout>
      );
    }

    if (activeTab === 'users') {
      if (!role) {
        return (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <p style={{ color: 'var(--color-text-muted)' }}>Select a role to view details</p>
          </div>
        );
      }

      // TODO: Backend should provide users_with_role and users_without_role like permissions do
      const usersWithRole = role.users || [];
      const usersWithoutRole = []; // TODO: Get from backend

      return (
        <SearchableAccessList
          title="Role Users"
          subtitle="Manage which users have this role"
          itemsWithAccess={usersWithRole}
          itemsWithoutAccess={usersWithoutRole}
          itemType="user"
          accessLabel="Role"
          onToggleAccess={(userId, currentHasAccess) => {
            console.log(`Toggle role for user ${userId}, currently has: ${currentHasAccess}`);
            // TODO: Implement API call to add/remove user from role
          }}
          renderItemContent={(user, hasRole) => (
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{capitalizeFirstLetter(user.username)}</h4>
                {user.full_name && (
                  <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>({capitalizeFirstLetter(user.full_name)})</span>
                )}
                {!user.is_active && (
                  <Badge variant="inactive">
                    Inactive
                  </Badge>
                )}
              </div>
              {/* Show user's permissions as badges */}
              <div className="mt-2">
                <div className="flex gap-1 overflow-hidden">
                  {user.permission_ids && user.permission_ids.length > 0 ? (
                    user.permission_ids.map((permissionId, idx) => (
                      <Badge key={idx} variant="gray" className="whitespace-nowrap flex-shrink-0">
                        {permissionId.replace(/_/g, ' ')}
                      </Badge>
                    ))
                  ) : (
                    <Badge variant="gray-muted" className="whitespace-nowrap flex-shrink-0">
                      No Permissions
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}
        />
      );
    }

    if (activeTab === 'products') {
      if (!role) {
        return (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <p style={{ color: 'var(--color-text-muted)' }}>Select a role to view details</p>
          </div>
        );
      }

      // TODO: Backend should provide products_with_access and products_without_access
      const productsWithAccess = []; // TODO: Get from backend
      const productsWithoutAccess = []; // TODO: Get from backend

      return (
        <SearchableAccessList
          title="Role Product Access"
          subtitle="Manage which products this role can access"
          itemsWithAccess={productsWithAccess}
          itemsWithoutAccess={productsWithoutAccess}
          itemType="product"
          accessLabel="Access"
          onToggleAccess={(productId, currentHasAccess) => {
            console.log(`Toggle access for product ${productId}, currently has: ${currentHasAccess}`);
            // TODO: Implement API call to grant/remove product access for role
          }}
          renderItemContent={(product, hasAccess) => (
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{capitalizeFirstLetter(product.name)}</h4>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{capitalizeFirstLetter(product.category)}</p>
            </div>
          )}
        />
      );
    }

    return null;
  };

  // Header actions
  const headerActions = role && onDelete ? [
    createDeleteAction(
      () => onDelete(role.id),
      `Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`,
      'Delete role'
    )
  ] : [];

  return (
    <BaseDetailView
      item={role}
      emptyStateIcon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      emptyStateTitle="No role selected"
      emptyStateSubtitle="Select a role from the sidebar to view details"
      headerContent={headerContent}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleSetActiveTab}
      headerActions={<DetailViewActionButtons actions={headerActions} />}
      renderContent={renderContent}
    />
  );
}
